import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { getLeadDetail, getManufacturers, getProductLines, getPricingTemplates, createQuote, API, getToken } from '@/lib/api';
import { ArrowLeft, FileText, Download, Loader2, DollarSign, Clock, Award, Package, Zap } from 'lucide-react';

// CRITICAL: These 3 tiers are FIXED and cannot be changed
const TIER_CONFIG = [
  { label: 'Good', product: 'ECO', desc: 'Essential protection', color: 'bg-[hsl(var(--secondary))] text-[var(--brand-slate-900)]' },
  { label: 'Better', product: 'Mr Glass', desc: 'Best value', color: 'bg-[var(--brand-seafoam-200)] text-[var(--brand-ocean-700)]', badge: 'Recommended' },
  { label: 'Premium', product: 'ES Windows', desc: 'Top performance', color: 'bg-[var(--brand-ocean-100)] text-[var(--brand-ocean-700)]' }
];

export default function AdminQuoteBuilder() {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [openings, setOpenings] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [productLines, setProductLines] = useState([]);
  const [pricingTemplates, setPricingTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [createdQuoteId, setCreatedQuoteId] = useState(null);
  const [activeTier, setActiveTier] = useState(0);

  // Initialize with exactly 3 tiers
  const [tiers, setTiers] = useState([
    { manufacturer_id: '', product_line_id: '', option_label: 'Good - ECO', estimated_price: 0, lead_time_text: '', scope_summary: '', notes: '' },
    { manufacturer_id: '', product_line_id: '', option_label: 'Better - Mr Glass', estimated_price: 0, lead_time_text: '', scope_summary: '', notes: '' },
    { manufacturer_id: '', product_line_id: '', option_label: 'Premium - ES Windows', estimated_price: 0, lead_time_text: '', scope_summary: '', notes: '' }
  ]);
  const [laborCost, setLaborCost] = useState(0);
  const [permitCost, setPermitCost] = useState(0);
  const [finishCost, setFinishCost] = useState(0);
  const [quoteNotes, setQuoteNotes] = useState('');
  const [disclaimer, setDisclaimer] = useState('Final pricing is subject to field verification, exact measurements, engineering requirements, product selection, permitting, and installation conditions. All quotes are valid for 30 days.');

  const loadData = useCallback(async () => {
    try {
      const [leadResp, mfrResp, plResp, ptResp] = await Promise.all([
        getLeadDetail(leadId),
        getManufacturers(),
        getProductLines(),
        getPricingTemplates()
      ]);
      setLead(leadResp.data.lead);
      setOpenings(leadResp.data.openings || []);
      setManufacturers(mfrResp.data);
      setProductLines(plResp.data);
      setPricingTemplates(ptResp.data);

      // Auto-assign manufacturers to tiers if available
      const activeMfrs = mfrResp.data.filter(m => m.active);
      if (activeMfrs.length >= 3) {
        setTiers(prev => prev.map((tier, i) => ({
          ...tier,
          manufacturer_id: activeMfrs[i]?.id || ''
        })));
      }
    } catch (err) {
      toast.error('Failed to load data');
    }
    setLoading(false);
  }, [leadId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const autoCalcPrice = (mfrId) => {
    if (!mfrId || openings.length === 0) return 0;
    const templates = pricingTemplates.filter(t => t.manufacturer_id === mfrId && t.active);
    let total = 0;
    for (const opening of openings) {
      const tpl = templates.find(t => t.opening_type === opening.opening_type);
      if (tpl) {
        total += (tpl.base_price || 0) * (opening.quantity || 1);
      } else {
        // Default price per opening if no template
        total += 500 * (opening.quantity || 1);
      }
    }
    return total;
  };

  const handleMfrChange = (index, mfrId) => {
    const price = autoCalcPrice(mfrId);
    const mfr = manufacturers.find(m => m.id === mfrId);
    const mfrPls = productLines.filter(pl => pl.manufacturer_id === mfrId);
    const primaryPl = mfrPls[0];

    setTiers(prev => prev.map((tier, i) => i === index ? {
      ...tier,
      manufacturer_id: mfrId,
      product_line_id: primaryPl?.id || '',
      estimated_price: price,
      lead_time_text: primaryPl ? `${primaryPl.lead_time_days} days` : '',
      scope_summary: `${openings.length} openings (${lead?.num_windows || 0}W / ${lead?.num_doors || 0}D) - ${mfr?.name || ''} ${primaryPl?.name || ''}`,
    } : tier));
  };

  const updateTier = (index, field, value) => {
    setTiers(prev => prev.map((tier, i) => i === index ? { ...tier, [field]: value } : tier));
  };

  const handleSaveQuote = async () => {
    const validTiers = tiers.filter(t => t.manufacturer_id);
    if (validTiers.length === 0) {
      toast.error('Please configure at least one tier with a manufacturer');
      return;
    }
    setSaving(true);
    try {
      const resp = await createQuote({
        lead_id: leadId,
        options: validTiers.map(t => ({
          ...t,
          estimated_price: parseFloat(t.estimated_price) || 0
        })),
        notes: quoteNotes,
        disclaimer,
        labor_cost: parseFloat(laborCost) || 0,
        permit_cost: parseFloat(permitCost) || 0,
        finish_work_cost: parseFloat(finishCost) || 0
      });
      setCreatedQuoteId(resp.data.id);
      toast.success(`Quote ${resp.data.quote_number} created successfully!`);
    } catch (err) {
      toast.error('Failed to create quote');
    }
    setSaving(false);
  };

  if (loading) return (
    <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center">
      <Loader2 className="h-6 w-6 animate-spin text-[var(--brand-ocean-700)]" />
    </div>
  );

  const totalEstimate = tiers.reduce((s, t) => s + (parseFloat(t.estimated_price) || 0), 0) + (parseFloat(laborCost) || 0) + (parseFloat(permitCost) || 0) + (parseFloat(finishCost) || 0);

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      {/* Nav */}
      <nav className="sticky top-0 z-50 bg-white border-b border-[hsl(var(--border))]">
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 flex items-center justify-between h-14">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate(`/admin/leads/${leadId}`)} className="text-[var(--brand-slate-600)] hover:text-[var(--brand-ocean-700)]">
              <ArrowLeft className="h-4 w-4 mr-1" /> Back to Lead
            </Button>
            <Separator orientation="vertical" className="h-5" />
            <div className="flex items-center gap-2">
              <Zap className="h-4 w-4 text-[var(--brand-ocean-700)]" />
              <span className="font-heading font-semibold text-sm text-[var(--brand-slate-900)]">Quote Builder</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {createdQuoteId && (
              <a href={`${API}/admin/quotes/${createdQuoteId}/pdf?token=${getToken()}`} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" size="sm" className="rounded-xl border-2" data-testid="admin-quote-export-pdf-button">
                  <Download className="h-4 w-4 mr-1" /> Download PDF
                </Button>
              </a>
            )}
            <Button onClick={handleSaveQuote} disabled={saving} className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-[hsl(var(--primary-foreground))] rounded-xl h-9 px-4 text-sm font-semibold">
              {saving ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Saving...</> : <><FileText className="h-4 w-4 mr-1" /> Save Quote</>}
            </Button>
          </div>
        </div>
      </nav>

      <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 py-6">
        {/* Lead Summary */}
        <Card className="rounded-2xl mb-6 border-2">
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4 text-sm">
              <span><strong className="text-[var(--brand-slate-900)]">Customer:</strong> <span className="text-[var(--brand-slate-600)]">{lead?.first_name} {lead?.last_name}</span></span>
              <span><strong className="text-[var(--brand-slate-900)]">Property:</strong> <span className="text-[var(--brand-slate-600)]">{lead?.property_type} - {lead?.project_type}</span></span>
              <span><strong className="text-[var(--brand-slate-900)]">Windows:</strong> <span className="text-[var(--brand-slate-600)]">{lead?.num_windows}</span> | <strong className="text-[var(--brand-slate-900)]">Doors:</strong> <span className="text-[var(--brand-slate-600)]">{lead?.num_doors}</span></span>
              <span><strong className="text-[var(--brand-slate-900)]">Openings:</strong> <span className="text-[var(--brand-slate-600)]">{openings.length}</span></span>
            </div>
          </CardContent>
        </Card>

        {/* 3-Tier Tabs - FIXED STRUCTURE */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-xl font-semibold text-[var(--brand-slate-900)]">Configure 3 Quote Tiers</h2>
            <Badge variant="outline" className="text-xs border-[var(--brand-ocean-600)] text-[var(--brand-ocean-600)]">3 Tiers Required</Badge>
          </div>
          
          {/* Tier Tabs */}
          <div className="flex gap-2 mb-4">
            {TIER_CONFIG.map((tier, i) => (
              <button
                key={tier.label}
                onClick={() => setActiveTier(i)}
                className={`flex-1 p-4 rounded-xl border-2 transition-all duration-200 ${
                  activeTier === i
                    ? 'border-[var(--brand-ocean-600)] bg-[var(--brand-ocean-100)]/30 shadow-sm'
                    : 'border-[hsl(var(--border))] hover:border-[var(--brand-ocean-600)]/50'
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <Badge className={tier.color}>{tier.label}</Badge>
                  {tier.badge && <Badge variant="outline" className="text-xs border-green-600 text-green-700 bg-green-50">{tier.badge}</Badge>}
                </div>
                <p className="text-xs text-[var(--brand-slate-600)] text-left mt-1">{tier.product}</p>
                <p className="text-xs text-[var(--brand-slate-600)] text-left">{tier.desc}</p>
              </button>
            ))}
          </div>

          {/* Active Tier Form */}
          <Card className="rounded-2xl border-2">
            <CardContent className="p-6 space-y-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-heading font-semibold text-lg text-[var(--brand-slate-900)]">
                  {TIER_CONFIG[activeTier].label} - {TIER_CONFIG[activeTier].product}
                </h3>
                <Badge className={TIER_CONFIG[activeTier].color}>{TIER_CONFIG[activeTier].label}</Badge>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Manufacturer</Label>
                  <Select value={tiers[activeTier].manufacturer_id} onValueChange={v => handleMfrChange(activeTier, v)}>
                    <SelectTrigger className="mt-1.5 border-2"><SelectValue placeholder="Select manufacturer" /></SelectTrigger>
                    <SelectContent>{manufacturers.filter(m => m.active).map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Product Line</Label>
                  <Select value={tiers[activeTier].product_line_id} onValueChange={v => updateTier(activeTier, 'product_line_id', v)}>
                    <SelectTrigger className="mt-1.5 border-2"><SelectValue placeholder="Select product line" /></SelectTrigger>
                    <SelectContent>{productLines.filter(pl => pl.manufacturer_id === tiers[activeTier].manufacturer_id).map(pl => <SelectItem key={pl.id} value={pl.id}>{pl.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="flex items-center gap-1"><DollarSign className="h-4 w-4" /> Estimated Price</Label>
                  <Input type="number" value={tiers[activeTier].estimated_price} onChange={e => updateTier(activeTier, 'estimated_price', e.target.value)} className="mt-1.5 border-2" />
                </div>
                <div>
                  <Label className="flex items-center gap-1"><Clock className="h-4 w-4" /> Lead Time</Label>
                  <Input value={tiers[activeTier].lead_time_text} onChange={e => updateTier(activeTier, 'lead_time_text', e.target.value)} placeholder="e.g., 6-8 weeks" className="mt-1.5 border-2" />
                </div>
              </div>
              
              <div>
                <Label>Scope Summary</Label>
                <Input value={tiers[activeTier].scope_summary} onChange={e => updateTier(activeTier, 'scope_summary', e.target.value)} placeholder="Brief description of what's included" className="mt-1.5 border-2" />
              </div>
              
              <div>
                <Label>Internal Notes (Optional)</Label>
                <Textarea value={tiers[activeTier].notes} onChange={e => updateTier(activeTier, 'notes', e.target.value)} rows={2} placeholder="Notes for internal use only" className="mt-1.5 border-2" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Additional Costs */}
        <Card className="rounded-2xl mt-6 border-2">
          <CardContent className="p-5">
            <h3 className="font-heading font-semibold mb-4 text-[var(--brand-slate-900)]">Additional Costs</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <Label className="flex items-center gap-1"><DollarSign className="h-3.5 w-3.5" /> Labor</Label>
                <Input type="number" value={laborCost} onChange={e => setLaborCost(e.target.value)} placeholder="0" className="mt-1.5 border-2" />
              </div>
              <div>
                <Label className="flex items-center gap-1"><DollarSign className="h-3.5 w-3.5" /> Permits</Label>
                <Input type="number" value={permitCost} onChange={e => setPermitCost(e.target.value)} placeholder="0" className="mt-1.5 border-2" />
              </div>
              <div>
                <Label className="flex items-center gap-1"><DollarSign className="h-3.5 w-3.5" /> Finish Work</Label>
                <Input type="number" value={finishCost} onChange={e => setFinishCost(e.target.value)} placeholder="0" className="mt-1.5 border-2" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Notes & Disclaimer */}
        <Card className="rounded-2xl mt-6 border-2">
          <CardContent className="p-5 space-y-4">
            <div>
              <Label>Quote Notes (Optional)</Label>
              <Textarea value={quoteNotes} onChange={e => setQuoteNotes(e.target.value)} rows={2} placeholder="Additional notes to include in the quote..." className="mt-1.5 border-2" />
            </div>
            <div>
              <Label>Disclaimer</Label>
              <Textarea value={disclaimer} onChange={e => setDisclaimer(e.target.value)} rows={2} className="mt-1.5 border-2" />
            </div>
          </CardContent>
        </Card>

        {/* Total */}
        <Card className="rounded-2xl mt-6 border-[var(--brand-ocean-600)] border-2 bg-gradient-to-br from-[var(--brand-ocean-100)]/30 to-white">
          <CardContent className="p-6">
            <div className="flex justify-between items-center">
              <span className="font-heading font-semibold text-lg text-[var(--brand-slate-900)]">Total Estimate</span>
              <span className="font-heading font-bold text-3xl text-[var(--brand-ocean-700)]">${totalEstimate.toLocaleString()}</span>
            </div>
            <div className="flex gap-6 mt-3 text-xs text-[var(--brand-slate-600)]">
              <span>Materials: <strong className="text-[var(--brand-slate-900)]">${tiers.reduce((s, t) => s + (parseFloat(t.estimated_price) || 0), 0).toLocaleString()}</strong></span>
              <span>Labor: <strong className="text-[var(--brand-slate-900)]">${(parseFloat(laborCost) || 0).toLocaleString()}</strong></span>
              <span>Permits: <strong className="text-[var(--brand-slate-900)]">${(parseFloat(permitCost) || 0).toLocaleString()}</strong></span>
              <span>Finish: <strong className="text-[var(--brand-slate-900)]">${(parseFloat(finishCost) || 0).toLocaleString()}</strong></span>
            </div>
          </CardContent>
        </Card>

        {/* Comparison Preview */}
        <div className="mt-6">
          <h3 className="font-heading font-semibold mb-3 text-[var(--brand-slate-900)]">Customer Preview (How tiers will appear)</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="admin-quote-comparison">
            {tiers
              .map((tier, originalIndex) => ({ tier, originalIndex }))
              .filter(({ tier }) => tier.manufacturer_id)
              .map(({ tier, originalIndex }) => {
                // IMPORTANT: use `originalIndex` (position before filtering), not the
                // filtered array's index — otherwise, once any tier before this one
                // is skipped (no manufacturer assigned yet), every subsequent tier's
                // TIER_CONFIG lookup shifts by one and shows the wrong label/color
                // (e.g. "Premium" tier rendering with the "Better" badge).
                const config = TIER_CONFIG[originalIndex];
                const mfr = manufacturers.find(m => m.id === tier.manufacturer_id);
                const pl = productLines.find(p => p.id === tier.product_line_id);
                return (
                  <Card key={tier.option_label || originalIndex} className={`rounded-2xl border-2 ${config.badge ? 'ring-2 ring-[var(--brand-seafoam-200)]' : ''}`}>
                    <CardContent className="p-5">
                      <div className="flex items-center justify-between mb-3">
                        <Badge className={config.color}>{config.label}</Badge>
                        {config.badge && <Badge variant="outline" className="text-xs border-green-600 text-green-700 bg-green-50">{config.badge}</Badge>}
                      </div>
                      <p className="font-heading font-bold text-2xl text-[var(--brand-ocean-700)] mb-1">${(parseFloat(tier.estimated_price) || 0).toLocaleString()}</p>
                      <p className="font-medium text-sm text-[var(--brand-slate-900)] mb-0.5">{mfr?.name}</p>
                      {pl && <p className="text-xs text-[var(--brand-slate-600)] mb-2">{pl.name}</p>}
                      <p className="text-xs text-[var(--brand-slate-600)]">{config.desc}</p>
                      {tier.lead_time_text && (
                        <div className="flex items-center gap-1 mt-3 text-xs text-[var(--brand-slate-600)]">
                          <Clock className="h-3 w-3" /> {tier.lead_time_text}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
          </div>
        </div>
      </div>
    </div>
  );
}
